import { useEffect, useState, type CSSProperties } from "react";
import {
  listAdminFlights,
  searchAdminPours,
  setFlightPours,
  generateFlightAi,
  saveFlightDescription,
  type FlightItem,
  type FlightPour,
  type SearchPourItem,
  type FlightAiSuggestion,
} from "../../lib/api";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";
import { usePhoneDetail } from "../../lib/usePhoneDetail";
import { useMediaQuery, PHONE_TAP_QUERY } from "../../lib/useMediaQuery";
import PhoneBackButton from "../../components/admin/PhoneBackButton";

export default function AdminFlights() {
  // Phone (<768): stack side-by-side panes (they overflowed the 343px content width).
  const isPhone = useIsMobile(768);
  // Phone (<768): the flight list or the editor, never both (Phase 4b). The first flight is
  // selected on load, so the phone view opens only on a tap, not on selection.
  const phone = usePhoneDetail(isPhone);
  // Where global.css's 44px tap rule applies, the pour row's ↑ ↓ ✕ are 44px wide, so the name
  // needs a larger basis before the controls wrap under it (Phase 4d: at 932×430 touch the old
  // 150px left "Hibiki Japanese Harmony" 181px and it broke). Tablets and wider keep 150px.
  const tapPhone = useMediaQuery(PHONE_TAP_QUERY);
  const [flights, setFlights] = useState<FlightItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Working Pours State (editable copy)
  const [workingPours, setWorkingPours] = useState<FlightPour[]>([]);
  const [dirty, setDirty] = useState(false);
  const [savingPours, setSavingPours] = useState(false);

  // Description Editor
  const [description, setDescription] = useState("");
  const [descDirty, setDescDirty] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);

  // Search Pour State
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchPourItem[]>([]);
  const [searching, setSearching] = useState(false);

  // AI Composition State
  const [aiGuidance, setAiGuidance] = useState("");
  const [aiSize, setAiSize] = useState<number>(3);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiProposal, setAiProposal] = useState<FlightAiSuggestion | null>(null);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const loadFlightsData = async (preserveSelectedId?: string) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await listAdminFlights();
      const list = res.flights || [];
      setFlights(list);
      const targetId = preserveSelectedId || selectedId || list[0]?.id || null;
      if (targetId) {
        selectFlight(targetId, list);
      }
    } catch (err: any) {
      setErrorMsg("Failed to load flights: " + (err?.message || "Error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlightsData();
  }, []);

  /** Returns false when the user keeps unsaved changes instead of switching flights. */
  const selectFlight = (id: string, list = flights): boolean => {
    if (dirty || descDirty) {
      if (!window.confirm("You have unsaved changes to this flight. Discard changes?")) {
        return false;
      }
    }
    setSelectedId(id);
    const f = list.find((x) => x.id === id);
    if (f) {
      const sorted = (f.pours || [])
        .slice()
        .sort((a, b) => (a.pour_order || 0) - (b.pour_order || 0))
        .map((p) => ({ ...p }));
      setWorkingPours(sorted);
      setDescription(f.description || "");
      setDirty(false);
      setDescDirty(false);
      setAiProposal(null);
      setSearchQuery("");
      setSearchResults([]);
    }
    return true;
  };

  // Search Debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchAdminPours(searchQuery.trim());
        setSearchResults(res.pours || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const selectedFlight = flights.find((f) => f.id === selectedId) || null;

  // Pour List Actions
  const handleAddPour = (pour: SearchPourItem) => {
    if (workingPours.some((p) => p.menu_item_id === pour.id)) {
      alert("This pour is already in the flight.");
      return;
    }
    const newPour: FlightPour = {
      menu_item_id: pour.id,
      pour_order: workingPours.length + 1,
      pour_size_oz: 1.0,
      pour_name: pour.name,
      square_pour_item_id: pour.square_pour_item_id,
      bottle_id: pour.bottle_id,
      bottle_name: pour.bottle_name,
      pours_remaining: pour.pours_remaining,
      status: pour.status,
    };
    setWorkingPours((prev) => [...prev, newPour]);
    setDirty(true);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemovePour = (idx: number) => {
    const updated = workingPours.filter((_, i) => i !== idx).map((p, i) => ({
      ...p,
      pour_order: i + 1,
    }));
    setWorkingPours(updated);
    setDirty(true);
  };

  const handleMovePour = (idx: number, delta: number) => {
    const targetIdx = idx + delta;
    if (targetIdx < 0 || targetIdx >= workingPours.length) return;
    const items = [...workingPours];
    const [moved] = items.splice(idx, 1);
    items.splice(targetIdx, 0, moved);
    const reordered = items.map((p, i) => ({ ...p, pour_order: i + 1 }));
    setWorkingPours(reordered);
    setDirty(true);
  };

  const handleSizeChange = (idx: number, sizeStr: string) => {
    const size = parseFloat(sizeStr) || 1.0;
    setWorkingPours((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, pour_size_oz: size } : p))
    );
    setDirty(true);
  };

  const handleSavePours = async () => {
    if (!selectedId) return;
    setSavingPours(true);
    try {
      await setFlightPours({
        flight_id: selectedId,
        pours: workingPours.map((p, idx) => ({
          menu_item_id: p.menu_item_id,
          pour_order: idx + 1,
          pour_size_oz: p.pour_size_oz || 1.0,
        })),
      });
      setDirty(false);
      showToast("Flight pour lineup saved.");
      await loadFlightsData(selectedId);
    } catch (err: any) {
      alert("Failed to save pours: " + (err?.message || "Error"));
    } finally {
      setSavingPours(false);
    }
  };

  // Description Actions
  const handleSaveDescription = async () => {
    if (!selectedId) return;
    setSavingDesc(true);
    try {
      await saveFlightDescription({
        flight_id: selectedId,
        description: description.trim(),
      });
      setDescDirty(false);
      showToast("Flight description updated.");
      await loadFlightsData(selectedId);
    } catch (err: any) {
      alert("Failed to save description: " + (err?.message || "Error"));
    } finally {
      setSavingDesc(false);
    }
  };

  // AI Composition
  const handleGenerateAi = async () => {
    if (!selectedId) return;
    setAiGenerating(true);
    setErrorMsg(null);
    try {
      const res = await generateFlightAi({
        flight_id: selectedId,
        guidance: aiGuidance.trim() || undefined,
        size: aiSize,
      });
      setAiProposal(res);
    } catch (err: any) {
      alert("AI generation failed: " + (err?.message || "Error"));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleApplyAiProposal = () => {
    if (!aiProposal) return;
    const newPours: FlightPour[] = (aiProposal.pours || []).map((p, idx) => ({
      menu_item_id: p.menu_item_id,
      pour_order: idx + 1,
      pour_size_oz: p.pour_size_oz || 1.0,
      pour_name: p.name,
      bottle_id: p.bottle_id,
      bottle_name: p.bottle_name,
      note: p.note,
    }));
    setWorkingPours(newPours);
    setDirty(true);
    if (aiProposal.description) {
      setDescription(aiProposal.description);
      setDescDirty(true);
    }
    setAiProposal(null);
    showToast("AI flight proposal applied. Click Save Lineup to commit.");
  };

  const fmtMoney = (cents: number | null | undefined) =>
    Number.isFinite(cents) ? "$" + ((cents || 0) / 100).toFixed(2) : "";

  return (
    <div style={styles.container}>
      <RecipeStudioTabs activeTab="flights" badgeCounts={{ flights: flights.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Flight Builder</h1>
          <p style={styles.subtitle}>
            Compose neat pour lineups, set pour sizes, and craft AI tasting notes
          </p>
        </div>
        <button
          style={styles.refreshBtn}
          onClick={() => loadFlightsData(selectedId || undefined)}
          disabled={loading}
        >
          {loading ? "..." : "↻ Refresh Flights"}
        </button>
      </div>

      {/* Messages */}
      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}
      {successMsg && <div style={styles.successBanner}>{successMsg}</div>}

      {/* Main Master-Detail Layout */}
      <div style={isPhone ? { ...styles.layout, gridTemplateColumns: "minmax(0, 1fr)" } : styles.layout}>
        {/* Left Sidebar: Flights List */}
        {phone.showList && <div style={styles.sidebar}>
          <div style={styles.sidebarHeading}>Preset Flights ({flights.length})</div>
          <div style={isPhone ? { ...styles.flightList, ...phoneListScroll } : styles.flightList}>
            {loading ? (
              <div style={styles.empty}>Loading flights...</div>
            ) : flights.length === 0 ? (
              <div style={styles.empty}>No flights found in catalog.</div>
            ) : (
              flights.map((f) => {
                const isActive = f.id === selectedId;
                const pourCount = (f.pours || []).length;

                return (
                  <div
                    key={f.id}
                    style={{
                      ...styles.flightCard,
                      ...(isActive ? styles.flightCardActive : {}),
                    }}
                    // On a phone, Back keeps unsaved edits, so re-opening the flight that's already
                    // selected just shows it again instead of asking to discard them.
                    onClick={() => { if ((isPhone && f.id === selectedId) || selectFlight(f.id)) phone.openDetail(); }}
                  >
                    <div style={styles.flightCardTop}>
                      <span style={styles.flightName}>{f.name.replace(/ Flight$/i, "")}</span>
                      <span style={styles.flightPrice}>{fmtMoney(f.pour_price_cents)}</span>
                    </div>
                    <div style={styles.flightMeta}>
                      <span
                        style={{
                          ...styles.countBadge,
                          ...(pourCount === 0 ? styles.countBadgeEmpty : {}),
                        }}
                      >
                        {pourCount} {pourCount === 1 ? "pour" : "pours"}
                      </span>
                      {f.square_pour_variation_id && (
                        <span style={styles.squareTag}>Square Synced</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>}

        {/* Right Area: Flight Composition Editor */}
        {phone.showDetail && <div style={styles.editorArea} ref={phone.detailRef}>
          {phone.phoneOpen && <PhoneBackButton label="Flights" onClick={phone.back} />}
          {selectedFlight ? (
            <div style={styles.editorCard}>
              {/* Flight Title & Price */}
              <div style={styles.editorHead}>
                <div>
                  <h2 style={styles.editorTitle}>
                    {selectedFlight.name.replace(/ Flight$/i, "")}{" "}
                    <span style={styles.flightSub}>Flight</span>
                  </h2>
                  <div style={styles.editorPrice}>
                    Price: {fmtMoney(selectedFlight.pour_price_cents)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    style={styles.saveBtn}
                    disabled={!dirty || savingPours}
                    onClick={handleSavePours}
                  >
                    {savingPours ? "Saving Lineup..." : "💾 Save Lineup"}
                  </button>
                </div>
              </div>

              {/* AI Composition Card */}
              <div style={styles.aiCard}>
                <div style={styles.aiHeader}>
                  <span style={styles.aiTitle}>✨ Compose with Sentinel AI</span>
                  <span style={styles.aiSub}>
                    Grounded on bottles open and on hand right now
                  </span>
                </div>
                <div style={styles.aiControls}>
                  <input
                    type="text"
                    style={styles.aiInput}
                    placeholder="Optional guidance (e.g. 'lean high-proof wheated bourbons, finish peated')"
                    value={aiGuidance}
                    onChange={(e) => setAiGuidance(e.target.value)}
                  />
                  <select
                    style={styles.aiSelect}
                    value={aiSize}
                    onChange={(e) => setAiSize(parseInt(e.target.value, 10) || 3)}
                  >
                    <option value={3}>3 Pours</option>
                    <option value={4}>4 Pours</option>
                    <option value={5}>5 Pours</option>
                  </select>
                  <button
                    style={styles.aiBtn}
                    disabled={aiGenerating}
                    onClick={handleGenerateAi}
                  >
                    {aiGenerating ? "Composing..." : "Generate Flight"}
                  </button>
                </div>

                {/* AI Proposal Box */}
                {aiProposal && (
                  <div style={styles.proposalBox}>
                    <div style={styles.proposalTop}>
                      <div>
                        <div style={styles.proposalTheme}>🌟 {aiProposal.theme}</div>
                        <div style={styles.proposalDesc}>{aiProposal.description}</div>
                      </div>
                      <button
                        style={styles.applyBtn}
                        onClick={handleApplyAiProposal}
                      >
                        Apply to Flight →
                      </button>
                    </div>
                    <div style={styles.proposalPours}>
                      {(aiProposal.pours || []).map((p, idx) => (
                        <div key={idx} style={styles.proposalPourItem}>
                          <strong>
                            {idx + 1}. {p.name}
                          </strong>
                          {p.note && <div style={styles.proposalPourNote}>{p.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Current Pours List */}
              <div style={styles.poursSection}>
                <div style={styles.sectionHeading}>
                  Flight Pours Lineup ({workingPours.length})
                </div>

                {workingPours.length === 0 ? (
                  <div style={styles.noPours}>
                    No pours assigned yet. Search below to add neat pours.
                  </div>
                ) : (
                  <div style={styles.poursList}>
                    {workingPours.map((p, idx) => (
                      <div key={idx} style={styles.pourRow}>
                        <div style={styles.orderBadge}>{idx + 1}</div>
                        <div style={tapPhone ? { ...styles.pourInfo, flex: "1 1 200px" } : styles.pourInfo}>
                          <div style={styles.pourName}>{p.pour_name}</div>
                          {p.bottle_name && (
                            <div style={styles.bottleSub}>Bottle: {p.bottle_name}</div>
                          )}
                          {p.pours_remaining !== undefined && p.pours_remaining !== null && (
                            <div
                              style={{
                                ...styles.stockNote,
                                ...(p.pours_remaining < 5 ? styles.stockLow : {}),
                              }}
                            >
                              {p.pours_remaining} pours left in bottle
                            </div>
                          )}
                        </div>

                        {/* Size + actions wrap together, as one unit, under the name when tight */}
                        <div style={styles.pourControls} data-pour-controls="">
                        {/* Pour Size Input */}
                        <div style={styles.sizeWrapper}>
                          <input
                            type="number"
                            step="0.25"
                            min="0.25"
                            max="3.0"
                            style={styles.sizeInput}
                            value={p.pour_size_oz || 1.0}
                            onChange={(e) => handleSizeChange(idx, e.target.value)}
                          />
                          <span style={styles.ozLabel}>oz</span>
                        </div>

                        {/* Actions */}
                        <div style={styles.orderActions}>
                          <button
                            style={styles.iconBtn}
                            disabled={idx === 0}
                            onClick={() => handleMovePour(idx, -1)}
                            title="Move Up"
                          >
                            ↑
                          </button>
                          <button
                            style={styles.iconBtn}
                            disabled={idx === workingPours.length - 1}
                            onClick={() => handleMovePour(idx, 1)}
                            title="Move Down"
                          >
                            ↓
                          </button>
                          <button
                            style={styles.delBtn}
                            onClick={() => handleRemovePour(idx)}
                            title="Remove Pour"
                          >
                            ✕
                          </button>
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Neat Pour Search */}
              <div style={styles.addSection}>
                <label style={styles.fieldLabel}>Add a Neat Pour to this Flight</label>
                <input
                  type="text"
                  style={styles.searchInput}
                  placeholder="Search neat pours in catalog..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searching && <div style={styles.searchStatus}>Searching...</div>}
                {searchResults.length > 0 && (
                  <div style={styles.searchResults}>
                    {searchResults.map((sp) => (
                      <div
                        key={sp.id}
                        style={styles.searchResultItem}
                        onClick={() => handleAddPour(sp)}
                      >
                        <div>
                          <div style={styles.searchResultName}>{sp.name}</div>
                          {sp.bottle_name && (
                            <div style={styles.searchResultBottle}>
                              Bottle: {sp.bottle_name}
                            </div>
                          )}
                        </div>
                        <button style={styles.addBtn}>+ Add Pour</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description / Tasting Notes Copy */}
              <div style={styles.descSection}>
                <div style={styles.descHeader}>
                  <label style={styles.fieldLabel}>Menu Copy & Tasting Notes</label>
                  <button
                    style={styles.saveDescBtn}
                    disabled={!descDirty || savingDesc}
                    onClick={handleSaveDescription}
                  >
                    {savingDesc ? "Saving..." : "Save Notes"}
                  </button>
                </div>
                <textarea
                  style={styles.descTextarea}
                  rows={4}
                  placeholder="Editorial tasting notes and description shown to guests..."
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDescDirty(true);
                  }}
                />
              </div>
            </div>
          ) : (
            <div style={styles.empty}>Select a flight from the left to edit.</div>
          )}
        </div>}
      </div>
    </div>
  );
}

// Phones: the flight list scrolls with the page, not inside its own box, which is removed while
// the editor is open, so its position was lost on Back (usePhoneDetail restores the page's scroll).
const phoneListScroll: CSSProperties = { maxHeight: "none", overflowY: "visible" };

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  refreshBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "320px 1fr",
    gap: 20,
    alignItems: "start",
  },
  sidebar: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 16,
  },
  sidebarHeading: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--muted, #8A7A66)",
    marginBottom: 12,
  },
  flightList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: "calc(100vh - 220px)",
    overflowY: "auto",
  },
  flightCard: {
    background: "rgba(0,0,0,0.2)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "border-color 0.15s ease",
  },
  flightCardActive: {
    borderColor: "var(--gold, #F9AF36)",
    background: "rgba(249, 175, 54, 0.08)",
  },
  flightCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  flightName: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
  },
  flightPrice: {
    fontSize: 12,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  flightMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  countBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold, #F9AF36)",
    padding: "2px 6px",
    borderRadius: 3,
  },
  countBadgeEmpty: {
    background: "rgba(138, 122, 102, 0.15)",
    color: "var(--muted, #8A7A66)",
  },
  squareTag: {
    fontSize: 10,
    color: "#60a5fa",
  },
  // minWidth 0: a grid item won't shrink below its content, so the editor (and the page) grew
  // to 1047px on the 1024 tablet and 807px at 768 (found in Phase 4b; it predates it). No-op
  // wherever the content fits, e.g. 1180 and 1440.
  editorArea: { minWidth: 0 },
  editorCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 20,
  },
  editorHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 14,
    marginBottom: 16,
  },
  editorTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    color: "var(--text, #F0E6D4)",
    margin: "0 0 4px 0",
  },
  flightSub: {
    fontSize: 14,
    color: "var(--gold, #F9AF36)",
    fontFamily: "system-ui",
    fontWeight: 400,
  },
  editorPrice: {
    fontSize: 13,
    color: "var(--gold, #F9AF36)",
    fontWeight: 600,
  },
  // One line, never squeezed: the title beside it is the part that wraps (L122).
  saveBtn: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  aiCard: {
    background: "rgba(249, 175, 54, 0.04)",
    border: "1px solid rgba(249, 175, 54, 0.2)",
    borderRadius: 8,
    padding: 14,
    marginBottom: 20,
  },
  aiHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  aiTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  aiSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  // Wraps the Generate button (and the select if needed) under the guidance input when it can't
  // get 120px. Measured: select + button + gaps are 230px, so where it wraps (768–834,
  // 1024–1074) the input is 196–262px (master: 128.5px; 54–120px before this wrap existed). In the
  // bands just above (835–863, 1075–1103) it is 121–149px; master is 128.5px at 835–842 and
  // 1075–1082, where it is up to 7.5px narrower here, while the pour names fit on one line.
  aiControls: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  // width 0 with flex 1: the input still fills its row at every width, but no longer counts its
  // default ~150px toward the row's minimum. That minimum (the AI card, 389px; everything else
  // in the editor needs ≤317px) is what pushed the page to 1047px on the 1024 tablet. Measured,
  // after two guesses that each traded the overflow for a squeeze (Phase 4b, verifier rounds 2–3).
  aiInput: {
    flex: "1 1 120px",
    width: 0,
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "7px 10px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
  },
  aiSelect: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "7px 8px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
  },
  aiBtn: {
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#111",
    borderRadius: 5,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  proposalBox: {
    marginTop: 12,
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 6,
    padding: 12,
  },
  proposalTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  proposalTheme: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  proposalDesc: {
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    marginTop: 3,
  },
  applyBtn: {
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#111",
    borderRadius: 4,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  proposalPours: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  proposalPourItem: {
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    background: "rgba(255,255,255,0.02)",
    padding: "4px 8px",
    borderRadius: 4,
  },
  proposalPourNote: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    fontStyle: "italic",
    marginTop: 2,
  },
  poursSection: {
    marginBottom: 20,
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    marginBottom: 10,
  },
  noPours: {
    textAlign: "center",
    padding: 24,
    color: "var(--muted, #8A7A66)",
    background: "rgba(0,0,0,0.2)",
    borderRadius: 6,
    border: "1px dashed var(--border, #2A1F14)",
    fontSize: 13,
  },
  poursList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  // Wraps when a 150px name can't fit beside the controls: the size input and buttons (one unit,
  // pourControls) then move under the name, which gets the full row instead of being squeezed or
  // pushing the page sideways. Measured: controls + badge + gaps are 217px, so the rows wrap at
  // about 768–850 and 1024–1090 and stay one row elsewhere (900, 1100+ unchanged from master).
  // A name longer than its column (e.g. "Hibiki Japanese Harmony", ~184px) still wraps onto two
  // lines where the row doesn't wrap (851–883, 1091–1123), exactly as it does on master there.
  pourRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    background: "rgba(0,0,0,0.2)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "10px 14px",
  },
  // flexShrink 0: beside a long bottle name the 24px pour number shrank to an 8px sliver once the
  // editor column could shrink (Phase 4b minWidth fix). No-op wherever the row fits.
  orderBadge: {
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: 12,
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold, #F9AF36)",
    fontSize: 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pourControls: { display: "flex", alignItems: "center", gap: 12, flexShrink: 0 },
  pourInfo: {
    flex: "1 1 150px",
    minWidth: 0,
  },
  pourName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text, #F0E6D4)",
  },
  bottleSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  stockNote: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  stockLow: {
    color: "#ff8b7b",
    fontWeight: 700,
  },
  sizeWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  sizeInput: {
    width: 50,
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 4,
    padding: "4px 6px",
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    textAlign: "right",
  },
  ozLabel: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  orderActions: {
    display: "flex",
    gap: 4,
  },
  iconBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--muted, #8A7A66)",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 11,
    cursor: "pointer",
  },
  delBtn: {
    background: "transparent",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    color: "#ff8b7b",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 11,
    cursor: "pointer",
  },
  addSection: {
    marginBottom: 20,
    borderTop: "1px solid var(--border, #2A1F14)",
    paddingTop: 16,
  },
  fieldLabel: {
    display: "block",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--muted, #8A7A66)",
    marginBottom: 6,
  },
  searchInput: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  searchStatus: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginTop: 6,
    fontStyle: "italic",
  },
  searchResults: {
    marginTop: 8,
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    maxHeight: 200,
    overflowY: "auto",
    background: "rgba(0,0,0,0.3)",
  },
  searchResultItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
    cursor: "pointer",
  },
  searchResultName: {
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
  },
  searchResultBottle: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  addBtn: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    color: "var(--gold, #F9AF36)",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  descSection: {
    borderTop: "1px solid var(--border, #2A1F14)",
    paddingTop: 16,
  },
  descHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  saveDescBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  descTextarea: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
    lineHeight: 1.5,
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
  },
};
